import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, VStack, HStack, Text, SimpleGrid, Spinner, Table, Thead, Tr, Th, Tbody, Td,
  Heading, Alert, AlertIcon, Input, Button, Tag, TagLabel, TagCloseButton, Wrap, WrapItem,
  Select, IconButton, Checkbox, useToast,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import AnalyticsTimeWindow from "./AnalyticsTimeWindow";
import MetricCard from "./MetricCard";
import { useAnalyticsQuery } from "../../hooks/useAnalyticsQuery";
import { formatMs, formatNumber } from "./analyticsFormatters";
import {
  UserListBulkAddBar,
  UserListManagerModal,
  useUserLists,
} from "../../userLists";
import { BASE_URLS } from "../env";

function _backendUrl() {
  const override = process.env.REACT_APP_BACKEND_URL;
  if (override && override.trim() !== "") return override;
  const hostname = window.location.hostname;
  return hostname === "localhost" || hostname === "127.0.0.1"
    ? BASE_URLS.local.backend
    : BASE_URLS.render.backend;
}

const FILTER_STORAGE_KEY = "analytics_excluded_user_patterns";
const MAX_SUGGESTIONS = 8;

function loadPatterns() {
  try {
    const raw = localStorage.getItem(FILTER_STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((x) => typeof x === "string" && x.trim()) : [];
  } catch {
    return [];
  }
}

function savePatterns(patterns) {
  try {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(patterns));
  } catch {}
}

function findScrollableAncestor(el) {
  let cur = el?.parentElement;
  while (cur) {
    const s = getComputedStyle(cur);
    if ((s.overflowY === "auto" || s.overflowY === "scroll") && cur.scrollHeight > cur.clientHeight) {
      return cur;
    }
    cur = cur.parentElement;
  }
  return null;
}

export default function AllUsersAnalytics({ timeWindow, onTimeWindowChange, onSelectUser, refreshNonce = 0 }) {
  const { data, loading, error } = useAnalyticsQuery({ tab: "all", timeWindow, scope: "all", refreshNonce });
  const summary = data?.summary || {};
  const topUsers = data?.top_users || [];
  const topPages = data?.top_pages || [];

  const [excludedPatterns, setExcludedPatterns] = useState(loadPatterns);
  const [filterInput, setFilterInput] = useState("");
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState("newest");
  const [roleFilter, setRoleFilter] = useState("all");
  const topUsersRef = useRef(null);
  const isFirstPageRender = useRef(true);
  const cancelRef = useRef([]);

  const [selectedUserIds, setSelectedUserIds] = useState(() => new Set());

  // Saved-list apply: quick-exclude everyone in a saved list (e.g. employees).
  // Members are looked up in the users table so people without activity in the
  // current window still get their email added as an exclude pattern.
  const { lists: savedLists, getMembers } = useUserLists();
  const [applyListId, setApplyListId] = useState("");
  const [applyingList, setApplyingList] = useState(false);
  const [listManagerOpen, setListManagerOpen] = useState(false);
  const toast = useToast();

  const toggleUserSelected = (id) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const clearSelection = () => setSelectedUserIds(new Set());

  useEffect(() => {
    if (isFirstPageRender.current) {
      isFirstPageRender.current = false;
      return;
    }
    const doScroll = () => {
      const target = topUsersRef.current;
      if (!target) return;
      const scroller = findScrollableAncestor(target);
      if (!scroller) {
        target.scrollIntoView({ block: "start" });
        return;
      }
      const targetTop = target.getBoundingClientRect().top;
      const scrollerTop = scroller.getBoundingClientRect().top;
      const offset = Math.max(0, targetTop - scrollerTop + scroller.scrollTop - 8);
      scroller.scrollTo({ top: offset });
    };
    doScroll();
    const t1 = requestAnimationFrame(() => {
      const t2 = requestAnimationFrame(doScroll);
      cancelRef.current.push(t2);
    });
    cancelRef.current.push(t1);
    return () => {
      cancelRef.current.forEach((id) => cancelAnimationFrame(id));
      cancelRef.current = [];
    };
  }, [page]);

  const goToPage = (p) => {
    if (topUsersRef.current) {
      topUsersRef.current.focus({ preventScroll: true });
    }
    setPage(p);
  };

  useEffect(() => {
    savePatterns(excludedPatterns);
  }, [excludedPatterns]);

  const addPatternValue = (value) => {
    const v = (value || "").trim();
    if (!v) return;
    if (excludedPatterns.some((p) => p.toLowerCase() === v.toLowerCase())) return;
    setExcludedPatterns([...excludedPatterns, v]);
    setPage(0);
  };

  const addPatternFromInput = () => {
    const v = filterInput.trim();
    if (!v) return;
    addPatternValue(v);
    setFilterInput("");
    setSuggestionsOpen(false);
  };

  const removePattern = (pat) => {
    setExcludedPatterns(excludedPatterns.filter((p) => p !== pat));
    setPage(0);
  };

  const clearAllPatterns = () => {
    setExcludedPatterns([]);
    setPage(0);
  };

  const applyExcludeList = async () => {
    if (!applyListId) return;
    setApplyingList(true);
    try {
      const memberIds = await getMembers(applyListId);
      if (!memberIds || memberIds.length === 0) {
        toast({ title: "List is empty", status: "info", duration: 2000 });
        return;
      }
      const idSet = new Set(memberIds.map((id) => String(id)));

      // First lookup in topUsers (already in memory). For any list members not
      // present there (e.g. people without activity in the current window),
      // fall back to /api/admin/signups/ which uses the service role and can
      // see every user. Frontend supabase client is blocked by users-table RLS.
      const lookups = new Map();
      for (const u of topUsers || []) {
        if (idSet.has(String(u.user_id))) {
          lookups.set(String(u.user_id), { email: u.email, name: u.name });
        }
      }
      const missingIds = memberIds.filter(
        (id) => !lookups.has(String(id)),
      );
      if (missingIds.length > 0) {
        try {
          const res = await fetch(`${_backendUrl()}/api/admin/signups/`);
          const json = await res.json().catch(() => ({}));
          const allUsers = Array.isArray(json.users) ? json.users : [];
          for (const u of allUsers) {
            const uid = String(u.id ?? u.user_id ?? "");
            if (idSet.has(uid) && !lookups.has(uid)) {
              lookups.set(uid, { email: u.email, name: u.name });
            }
          }
        } catch (e) {
          console.warn("[ExcludeList] signups fallback failed:", e);
        }
      }

      const newPatterns = [];
      for (const [, info] of lookups) {
        const v = ((info.name && String(info.name).trim()) || (info.email && String(info.email).trim()) || "").trim();
        if (v) newPatterns.push(v);
      }

      let added = 0;
      setExcludedPatterns((prev) => {
        const seen = new Set(prev.map((p) => p.toLowerCase()));
        const next = [...prev];
        for (const v of newPatterns) {
          if (!seen.has(v.toLowerCase())) {
            next.push(v);
            seen.add(v.toLowerCase());
            added += 1;
          }
        }
        return next;
      });
      setPage(0);
      const listName =
        savedLists.find((l) => l.id === applyListId)?.name || "list";
      const unresolved = memberIds.length - lookups.size;
      const resolvedNote =
        unresolved > 0 ? ` · ${unresolved} member${unresolved === 1 ? "" : "s"} not found` : "";
      toast({
        title:
          added === 0
            ? "No new users to exclude"
            : `Excluded ${added} user${added === 1 ? "" : "s"}`,
        description: `From "${listName}"${resolvedNote}`,
        status: added === 0 ? "info" : "success",
        duration: 2500,
      });
    } catch (e) {
      toast({
        title: "Apply list failed",
        description: String(e?.message || e),
        status: "error",
        duration: 4000,
      });
    } finally {
      setApplyingList(false);
    }
  };

  const userPoolForManager = useMemo(
    () =>
      (topUsers || []).map((u) => ({
        id: u.user_id,
        name: u.name || "",
        email: u.email || "",
      })),
    [topUsers],
  );

  // Role tokens come back as "STUDENT,SOC_WORK,public" in some schemas
  // (public.users.role can be a comma list). Treat each token as a possible
  // match for the dropdown so a user with role "STUDENT,public" still matches
  // when the admin picks "STUDENT".
  const userRoleTokens = (u) => {
    const raw = (u?.role || "").trim();
    if (!raw) return [];
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  };

  const availableRoles = useMemo(() => {
    const seen = new Set();
    for (const u of topUsers) {
      for (const tok of userRoleTokens(u)) seen.add(tok);
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [topUsers]);

  const filteredUsers = useMemo(() => {
    const lowered = excludedPatterns.map((p) => p.toLowerCase());
    const base = topUsers.filter((u) => {
      if (lowered.length) {
        const name = (u.name || "").toLowerCase();
        const email = (u.email || "").toLowerCase();
        if (lowered.some((p) => (name && name.includes(p)) || (email && email.includes(p)))) {
          return false;
        }
      }
      if (roleFilter !== "all") {
        const tokens = userRoleTokens(u);
        if (!tokens.includes(roleFilter)) return false;
      }
      return true;
    });
    if (sortBy === "newest") {
      return [...base].sort((a, b) => {
        const at = a.created_at ? Date.parse(a.created_at) : 0;
        const bt = b.created_at ? Date.parse(b.created_at) : 0;
        return bt - at;
      });
    }
    if (sortBy === "recordings") {
      return [...base].sort((a, b) => (b.audio_record_count || 0) - (a.audio_record_count || 0));
    }
    if (sortBy === "ai_queries") {
      return [...base].sort((a, b) => (b.ai_query_count || 0) - (a.ai_query_count || 0));
    }
    if (sortBy === "transcripts") {
      return [...base].sort((a, b) => (b.transcript_count || 0) - (a.transcript_count || 0));
    }
    if (sortBy === "document_edits") {
      return [...base].sort((a, b) => (b.document_edit_count || 0) - (a.document_edit_count || 0));
    }
    // "rows" — sort by schedule_rows
    return [...base].sort((a, b) => (b.schedule_rows || 0) - (a.schedule_rows || 0));
  }, [topUsers, excludedPatterns, sortBy, roleFilter]);

  const suggestions = useMemo(() => {
    const q = filterInput.trim().toLowerCase();
    if (!q) return [];
    const excludedLower = excludedPatterns.map((p) => p.toLowerCase());
    const seen = new Set();
    const out = [];
    for (const u of topUsers) {
      const name = (u.name || "").trim();
      const email = (u.email || "").trim();
      const key = `${name}|${email}`;
      if (seen.has(key)) continue;
      const nameMatches = name && name.toLowerCase().includes(q);
      const emailMatches = email && email.toLowerCase().includes(q);
      if (!nameMatches && !emailMatches) continue;
      const primary = name || email;
      if (excludedLower.some((p) => primary.toLowerCase().includes(p))) continue;
      seen.add(key);
      out.push({ id: u.user_id, primary, secondary: name && email && name !== email ? email : null });
      if (out.length >= MAX_SUGGESTIONS) break;
    }
    return out;
  }, [filterInput, topUsers, excludedPatterns]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const currentPage = Math.min(page, totalPages - 1);
  const pagedUsers = filteredUsers.slice(currentPage * pageSize, currentPage * pageSize + pageSize);
  const excludedCount = topUsers.length - filteredUsers.length;

  // Reset checkbox selection whenever the visible page changes.
  // This effect must live after `currentPage` is computed above.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { clearSelection(); }, [currentPage]);

  return (
    <VStack align="stretch" spacing={5}>
      <AnalyticsTimeWindow value={timeWindow} onChange={onTimeWindowChange} />

      {error && (
        <Alert status="error"><AlertIcon />Failed to load analytics: {String(error.message || error)}</Alert>
      )}

      {loading && !data ? (
        <Box textAlign="center" py={10}><Spinner /></Box>
      ) : (
        <>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
            <MetricCard label="Active users" value={formatNumber(summary.active_users)} />
            <MetricCard label="Login sessions" value={formatNumber(summary.total_sessions)}
              helpText="session_start events" color="green.500" />
            <MetricCard label="Avg session time" value={formatMs(summary.avg_session_ms)} color="purple.500" />
            <MetricCard label="AI queries" value={formatNumber(summary.total_ai_queries)}
              helpText={`${(summary.avg_ai_queries_per_user || 0).toFixed(1)} / user`} color="orange.500" />
            <MetricCard label="Recordings" value={formatNumber(summary.total_audios)}
              helpText={`${(summary.avg_audios_per_user || 0).toFixed(1)} / user · avg ${formatMs(summary.avg_audio_ms)}`}
              color="red.500" />
            <MetricCard label="Transcripts" value={formatNumber(summary.total_transcripts)}
              helpText={`${(summary.avg_transcripts_per_user || 0).toFixed(1)} / user`} color="teal.500" />
            <MetricCard label="Document edits" value={formatNumber(summary.total_document_edits)} />
            <MetricCard label="Form submits" value={formatNumber(summary.total_form_submits)} />
          </SimpleGrid>

          <Box ref={topUsersRef} scrollMarginTop="8" tabIndex={-1} outline="none">
            <Heading size="sm" mb={2}>Top users</Heading>

            <VStack align="stretch" spacing={2} mb={3}>
              <HStack position="relative">
                <Box flex="1" position="relative">
                  <Input
                    size="sm"
                    placeholder="Exclude by name or email (e.g. diego, @mycompany.com)"
                    value={filterInput}
                    onChange={(e) => {
                      setFilterInput(e.target.value);
                      setSuggestionsOpen(true);
                    }}
                    onFocus={() => setSuggestionsOpen(true)}
                    onBlur={() => setTimeout(() => setSuggestionsOpen(false), 150)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addPatternFromInput();
                      } else if (e.key === "Escape") {
                        setSuggestionsOpen(false);
                      }
                    }}
                  />
                  {suggestionsOpen && suggestions.length > 0 && (
                    <Box
                      position="absolute"
                      top="100%"
                      left={0}
                      right={0}
                      mt={1}
                      bg="white"
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                      boxShadow="md"
                      zIndex={10}
                      maxH="260px"
                      overflowY="auto"
                    >
                      {suggestions.map((s) => (
                        <HStack
                          key={s.id}
                          px={3}
                          py={2}
                          justify="space-between"
                          cursor="pointer"
                          _hover={{ bg: "gray.50" }}
                          borderBottomWidth="1px"
                          borderBottomColor="gray.100"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            addPatternValue(s.primary);
                            setFilterInput("");
                            setSuggestionsOpen(false);
                          }}
                        >
                          <Box flex="1" minW={0}>
                            <Text fontSize="sm" noOfLines={1}>{s.primary}</Text>
                            {s.secondary && (
                              <Text fontSize="xs" color="gray.600" noOfLines={1}>{s.secondary}</Text>
                            )}
                          </Box>
                          <Text fontSize="xs" color="red.500" fontWeight="medium">
                            Filter out
                          </Text>
                        </HStack>
                      ))}
                    </Box>
                  )}
                </Box>
                <Button size="sm" colorScheme="blue" onClick={addPatternFromInput} isDisabled={!filterInput.trim()}>
                  Filter out
                </Button>
                {excludedPatterns.length > 0 && (
                  <Button size="sm" variant="ghost" onClick={clearAllPatterns}>
                    Clear all
                  </Button>
                )}
              </HStack>
              {excludedPatterns.length > 0 && (
                <Wrap spacing={2}>
                  {excludedPatterns.map((pat) => (
                    <WrapItem key={pat}>
                      <Tag size="sm" colorScheme="red" variant="subtle">
                        <TagLabel>{pat}</TagLabel>
                        <TagCloseButton onClick={() => removePattern(pat)} />
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              )}
              <HStack spacing={2} flexWrap="wrap">
                <Text fontSize="xs" color="gray.600" whiteSpace="nowrap">
                  Apply saved list:
                </Text>
                <Select
                  size="sm"
                  width="220px"
                  placeholder="— Pick a list —"
                  value={applyListId}
                  onChange={(e) => setApplyListId(e.target.value)}
                >
                  {savedLists.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name} ({l.member_count})
                    </option>
                  ))}
                </Select>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={applyExcludeList}
                  isDisabled={!applyListId || applyingList}
                  isLoading={applyingList}
                >
                  Exclude all
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setListManagerOpen(true)}
                >
                  Manage lists
                </Button>
              </HStack>
              <HStack justify="space-between" flexWrap="wrap" align="center">
                <Text fontSize="xs" color="gray.600">
                  Showing {filteredUsers.length} of {topUsers.length} users
                  {excludedCount > 0 && ` · ${excludedCount} hidden by filter`}
                </Text>
                <HStack spacing={3} align="center" flexWrap="wrap">
                  <HStack spacing={2} align="center">
                    <Text fontSize="xs" color="gray.600" whiteSpace="nowrap">Role:</Text>
                    <Select
                      size="xs"
                      width="auto"
                      value={roleFilter}
                      onChange={(e) => {
                        setRoleFilter(e.target.value);
                        setPage(0);
                      }}
                    >
                      <option value="all">All roles</option>
                      {availableRoles.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </Select>
                  </HStack>
                  <HStack spacing={2} align="center">
                    <Text fontSize="xs" color="gray.600" whiteSpace="nowrap">Sort by:</Text>
                    <Select
                      size="xs"
                      width="auto"
                      value={sortBy}
                      onChange={(e) => {
                        setSortBy(e.target.value);
                        setPage(0);
                      }}
                    >
                      <option value="newest">Newest</option>
                      <option value="rows">Most rows</option>
                      <option value="recordings">Most recordings</option>
                      <option value="ai_queries">Most AI queries</option>
                      <option value="transcripts">Most transcripts</option>
                      <option value="document_edits">Most document edits</option>
                    </Select>
                  </HStack>
                </HStack>
              </HStack>
            </VStack>

            {filteredUsers.length === 0 ? (
              <Text fontSize="sm" color="gray.500">
                {topUsers.length === 0 ? "No user activity yet." : "All users are filtered out."}
              </Text>
            ) : (
              <>
                <Box overflowX="auto">
                  <Table size="sm" variant="striped">
                    <Thead>
                      <Tr>
                        <Th width="36px">
                          <Checkbox
                            isChecked={
                              selectedUserIds.size > 0 &&
                              selectedUserIds.size === pagedUsers.length
                            }
                            isIndeterminate={
                              selectedUserIds.size > 0 && selectedUserIds.size < pagedUsers.length
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedUserIds(new Set(pagedUsers.map((u) => String(u.user_id))));
                              } else {
                                clearSelection();
                              }
                            }}
                          />
                        </Th>
                        <Th>User</Th>
                        <Th>Tenant</Th>
                        <Th isNumeric>Sessions</Th>
                        <Th isNumeric>Total active</Th>
                        <Th isNumeric>Recordings</Th>
                        <Th isNumeric>Schedule rows</Th>
                        <Th width="1%"></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {pagedUsers.map((u) => {
                        const label = u.name || u.email || "—";
                        return (
                          <Tr
                            key={u.user_id}
                            cursor={onSelectUser ? "pointer" : "default"}
                            _hover={onSelectUser ? { bg: "blue.50" } : undefined}
                            onClick={onSelectUser ? () => onSelectUser(u.user_id) : undefined}
                            title={onSelectUser ? "View individual analytics" : undefined}
                          >
                            <Td>
                              <Checkbox
                                isChecked={selectedUserIds.has(String(u.user_id))}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  toggleUserSelected(u.user_id);
                                }}
                              />
                            </Td>
                            <Td color={onSelectUser ? "blue.600" : undefined}
                                textDecoration={onSelectUser ? "underline" : undefined}>
                              {label}
                            </Td>
                            <Td fontSize="xs" color="gray.600">{u.schema || "—"}</Td>
                            <Td isNumeric>{formatNumber(u.sessions)}</Td>
                            <Td isNumeric>{formatMs(u.total_ms)}</Td>
                            <Td isNumeric>{formatNumber(u.audio_record_count)}</Td>
                            <Td isNumeric>{formatNumber(u.schedule_rows)}</Td>
                            <Td textAlign="right">
                              <IconButton
                                aria-label={`Filter out ${label}`}
                                title={`Filter out ${label}`}
                                icon={<CloseIcon boxSize={2.5} />}
                                size="xs"
                                variant="ghost"
                                colorScheme="red"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addPatternValue(label);
                                }}
                              />
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>

                <HStack mt={3} justify="space-between" flexWrap="wrap">
                  <HStack spacing={2}>
                    <Button size="xs" onClick={() => goToPage(0)} isDisabled={currentPage === 0}>
                      « First
                    </Button>
                    <Button size="xs" onClick={() => goToPage(currentPage - 1)} isDisabled={currentPage === 0}>
                      ‹ Prev
                    </Button>
                    <Text fontSize="sm">
                      Page {currentPage + 1} of {totalPages}
                    </Text>
                    <Button size="xs" onClick={() => goToPage(currentPage + 1)} isDisabled={currentPage >= totalPages - 1}>
                      Next ›
                    </Button>
                    <Button size="xs" onClick={() => goToPage(totalPages - 1)} isDisabled={currentPage >= totalPages - 1}>
                      Last »
                    </Button>
                  </HStack>
                  <HStack spacing={2}>
                    <Text fontSize="xs" color="gray.600">Rows:</Text>
                    <Select
                      size="xs"
                      width="auto"
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(0);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </Select>
                  </HStack>
                </HStack>
              </>
            )}
          </Box>

          <Box>
            <Heading size="sm" mb={2}>Top pages by visits</Heading>
            {topPages.length === 0 ? (
              <Text fontSize="sm" color="gray.500">No page-visit data yet.</Text>
            ) : (
              <Box overflowX="auto">
                <Table size="sm" variant="striped">
                  <Thead>
                    <Tr>
                      <Th>Page</Th>
                      <Th isNumeric>Visits</Th>
                      <Th isNumeric>Total time</Th>
                      <Th isNumeric>Avg time</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {topPages.map((p) => (
                      <Tr key={p.page}>
                        <Td>{p.page}</Td>
                        <Td isNumeric>{formatNumber(p.visits)}</Td>
                        <Td isNumeric>{formatMs(p.total_ms)}</Td>
                        <Td isNumeric>{formatMs(p.avg_ms)}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>
        </>
      )}
      <UserListBulkAddBar
        selectedUserIds={selectedUserIds}
        onClearSelection={clearSelection}
      />
      <UserListManagerModal
        isOpen={listManagerOpen}
        onClose={() => setListManagerOpen(false)}
        userPool={userPoolForManager}
      />
    </VStack>
  );
}
